/* Tiloli carousel nav: progress indicator + prev/next arrows, driving the native
   Horizon slideshow scroll container (slideshow-slides). Progress bar is non-interactive.

   Also drives ANY plain overflow-x scroller (mega-menu selections rail, …) when the host
   opts in with three attributes — this keeps one nav engine instead of a second one:
     [data-cn-scope]    sur un ancêtre  → borne la recherche (obligatoire s'il y a plusieurs
                                          carrousels sur la page, ex. les 2 panneaux méga)
     [data-cn-scroller] sur le scroller → l'élément qui déborde en x
     data-cn-slide="…"  sur ce <tiloli-carousel-nav> → sélecteur CSS d'une "slide" (défaut :
                                          les slides natives Horizon) */
class TiloliCarouselNav extends HTMLElement {
  connectedCallback() {
    const scope = this.closest('[data-cn-scope]')
      || this.closest('[data-testid="product-list"]')
      || this.closest('.section')
      || document;
    this.slideSel = this.getAttribute('data-cn-slide') || '.resource-list__slide, slideshow-slide';
    this.scroller = scope.querySelector('[data-cn-scroller]')
      || scope.querySelector('slideshow-slides')
      || scope.querySelector('[ref="scroller"]')
      || scope.querySelector('.resource-list__carousel');
    if (!this.scroller) return; // keep the bar visible even if we can't wire the scroller

    this.thumb = this.querySelector('[data-cn-thumb]');
    this.prev = this.querySelector('[data-cn-prev]');
    this.next = this.querySelector('[data-cn-next]');
    this.dotsWrap = this.querySelector('[data-cn-dots]');

    const step = () => {
      const slide = this.scroller.querySelector(this.slideSel);
      const gap = parseFloat(getComputedStyle(this.scroller).columnGap || '20') || 20;
      return slide ? slide.getBoundingClientRect().width + gap : this.scroller.clientWidth * 0.8;
    };

    this.prev.addEventListener('click', () => this.scroller.scrollBy({ left: -step(), behavior: 'smooth' }));
    this.next.addEventListener('click', () => this.scroller.scrollBy({ left: step(), behavior: 'smooth' }));

    // mobile pagination dots — one per slide, click scrolls to it
    this.dots = [];
    const slides = [...this.scroller.querySelectorAll(this.slideSel)];
    if (this.dotsWrap && slides.length > 1) {
      slides.forEach((s, i) => {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'tlcn__dot';
        d.setAttribute('aria-label', 'Slide ' + (i + 1));
        d.addEventListener('click', () => this.scroller.scrollTo({ left: i * step(), behavior: 'smooth' }));
        this.dotsWrap.appendChild(d);
        this.dots.push(d);
      });
    }

    this.update = () => {
      const max = this.scroller.scrollWidth - this.scroller.clientWidth;
      const ratio = this.scroller.scrollWidth > 0 ? this.scroller.clientWidth / this.scroller.scrollWidth : 1;
      const w = Math.max(12, Math.min(100, ratio * 100));
      const pos = max > 0 ? this.scroller.scrollLeft / max : 0;
      this.thumb.style.width = w + '%';
      this.thumb.style.left = (pos * (100 - w)) + '%';
      this.prev.disabled = this.scroller.scrollLeft <= 2;
      this.next.disabled = this.scroller.scrollLeft >= max - 2;
      if (this.dots.length) {
        const s = step();
        const idx = Math.max(0, Math.min(this.dots.length - 1, s > 0 ? Math.round(this.scroller.scrollLeft / s) : 0));
        this.dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      }
    };

    this.scroller.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update);
    window.addEventListener('load', this.update);

    // scrollWidth is under-reported at first paint (content-visibility:auto + lazy images),
    // which leaves the thumb stuck at 100% (looks like a full / absent bar). Recompute a few times.
    requestAnimationFrame(this.update);
    [120, 400, 1000].forEach((t) => setTimeout(this.update, t));
    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(this.update);
      this._ro.observe(this.scroller);
    }
    this.update();
  }
  disconnectedCallback() {
    window.removeEventListener('resize', this.update);
    window.removeEventListener('load', this.update);
    if (this._ro) this._ro.disconnect();
  }
}
/* Le snippet peut être rendu plusieurs fois par page (2 panneaux méga, plusieurs
   product-list) : chaque <script src> s'exécute, donc on garde le define idempotent. */
if (!customElements.get('tiloli-carousel-nav')) customElements.define('tiloli-carousel-nav', TiloliCarouselNav);
